import React, { useState, useEffect, useContext } from "react";
import { Button, Alert, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { AppContext } from "../../context";
import storeApi from "../storeApi";
import ProductDetailModal from "../../catalog/ProductDetailModal";
import PriceOrLogin from "./PriceOrLogin";
import { trackViewCart, trackRemoveFromCart } from "../../common/analytics";

const CART_PANE_WIDTH = 300;
const MOBILE_BREAKPOINT = 768;

/** Resolve store catalog product identifier for `GET /store/products/:code` (nested `product` or line fields). */
function storeCartLineProductId(line) {
  if (!line || typeof line !== "object") return undefined;
  const nested = line.product;
  if (nested && typeof nested === "object") {
    const id = nested.id;
    if (id != null && id !== "") return String(id);
  }
  const sid = line.store_product_id;
  if (sid != null && sid !== "") return String(sid);
  return undefined;
}

const Cart = ({ isOpen, toggle }) => {
  const { cart, setCart, setCartUuid, currentUser, cartUuid } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ total_items: 0, price: null });
  const [itemToRemove, setItemToRemove] = useState(null);
  const [selectedCartItem, setSelectedCartItem] = useState(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      setError(null);
      const options = cartUuid ? { cartUuid } : {};
      const response = await storeApi.cartAPI.getCart(options);
      const data = response?.data ?? response;
      const uuid = data?.cart_uuid;
      if (uuid) {
        localStorage.setItem("store_cart_uuid", uuid);
        setCartUuid(uuid);
      }
      const items = data?.room?.modules ?? data?.order?.items ?? [];
      setCart(Array.isArray(items) ? items : []);
      const sum = data?.summary ?? {};
      setSummary({
        total_items: sum.total_items ?? items.length ?? 0,
        price: sum.price != null ? parseFloat(sum.price) : null,
      });
      if (items?.length > 0) trackViewCart(items);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentUser) fetchCart();
  }, [isOpen, currentUser, cartUuid]);

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      setItemToRemove(null);
      const options = cartUuid ? { cartUuid } : {};
      const response = await storeApi.cartAPI.removeCartItem(itemId, options);
      if (response?.success !== false) {
        const removedItem = cart.find((item) => item.id === itemId);
        if (removedItem) trackRemoveFromCart(removedItem, removedItem.quantity || 1);
        await fetchCart();
      } else {
        setError("Failed to remove item");
      }
    } catch {
      setError("Failed to remove item");
    } finally {
      setLoading(false);
    }
  };

  const itemCount = Array.isArray(cart) ? cart.length : 0;
  if (!isOpen) return null;

  return (
    <>
      <div
        className="store-cart-pane-backdrop"
        onClick={toggle}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 1040 }}
        aria-hidden="true"
      />
      <div
        className="store-cart-pane bg-white shadow"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          left: isMobile ? 0 : undefined,
          width: isMobile ? "100%" : CART_PANE_WIDTH,
          maxWidth: "100vw",
          height: "100vh",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="d-flex align-items-center justify-content-between border-bottom p-3">
          <span className="fw-semibold">
            <i className="fas fa-shopping-cart me-2" />
            Cart {itemCount > 0 ? `(${itemCount})` : ""}
          </span>
          <Button color="link" className="p-0 text-dark" onClick={toggle} aria-label="Close cart">
            <i className="fas fa-times fs-5" />
          </Button>
        </div>
        {currentUser && itemCount > 0 && (
          <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2 bg-light">
            <span className="fw-semibold small">Total</span>
            <PriceOrLogin price={summary.price} className="fw-bold small" />
          </div>
        )}
        <div className="flex-grow-1 overflow-auto p-3">
          {!currentUser ? (
            <Alert color="info" className="mb-0">
              <i className="fas fa-lock me-2" />
              Please login to view your cart
            </Alert>
          ) : loading && itemCount === 0 ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : error && itemCount === 0 ? (
            <Alert color="danger">{error}</Alert>
          ) : itemCount === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-shopping-cart fa-3x text-muted mb-3" />
              <h6 className="text-muted">Your cart is empty</h6>
            </div>
          ) : (
            <>
              <div className="cart-items mb-3">
                {(cart || []).map((item) => {
                  const name = item.product_name ?? item.name ?? "Product";
                  const img = item.product_image ?? item.image ?? item.product_image_url;
                  return (
                    <div key={item.id} className="cart-item border-bottom pb-3 mb-3">
                      <div className="d-flex gap-2">
                        <div
                          className="flex-shrink-0 rounded overflow-hidden bg-light"
                          style={{ width: 56, height: 56, cursor: "pointer" }}
                          onClick={() => setSelectedCartItem(item)}
                          role="button"
                          tabIndex={0}
                        >
                          <img src={img} alt={name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-semibold text-dark small text-truncate">{name}</div>
                        </div>
                        <div className="flex-shrink-0 text-end">
                          <div className="small fw-semibold">
                            <PriceOrLogin price={item.price != null ? parseFloat(item.price) : null} />
                          </div>
                          <Button
                            color="link"
                            className="text-secondary p-0 small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemToRemove(item.id);
                            }}
                            disabled={loading}
                          >
                            <i className="fas fa-trash" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="cart-summary border-top pt-3">
                <div className="d-flex justify-content-between fw-bold">
                  <span>Total</span>
                  <PriceOrLogin price={summary.price} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ProductDetailModal
        key={selectedCartItem?.id ?? "closed"}
        isOpen={
          selectedCartItem != null &&
          (selectedCartItem.product != null ||
            storeCartLineProductId(selectedCartItem) != null)
        }
        product={selectedCartItem?.product ?? null}
        productId={storeCartLineProductId(selectedCartItem)}
        initialConfig={selectedCartItem?.config ?? {}}
        onClose={() => setSelectedCartItem(null)}
      />

      <Modal isOpen={itemToRemove != null} toggle={() => setItemToRemove(null)} size="sm">
        <ModalHeader toggle={() => setItemToRemove(null)}>Remove from cart?</ModalHeader>
        <ModalBody>Remove this item from your cart?</ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setItemToRemove(null)}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={() => itemToRemove != null && removeFromCart(itemToRemove)}
            disabled={loading}
          >
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Cart;
